
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiMessageCircle } from 'react-icons/fi';
import { getScrollBehavior } from '@/utils/scroll';

export default function FloatingCTA() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show button after scrolling 200px
      const scrollPosition = window.scrollY;
      const hasScrolledEnough = scrollPosition > 200;

      // Hide when contact section is in view
      const contactSection = document.getElementById('contact');
      let isContactInView = false;

      if (contactSection) {
        const rect = contactSection.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        // Consider contact section "in view" when at least 30% is visible
        isContactInView = rect.top < viewportHeight * 0.7 && rect.bottom > 0;
      }

      setIsVisible(hasScrolledEnough && !isContactInView);
    };

    // Layout reads once per frame at most, instead of on every scroll event
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        handleScroll();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    // Check initial position
    handleScroll();

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const handleClick = () => {
    // Smooth scroll to contact section
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: getScrollBehavior() });
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          initial={{ opacity: 0, y: 20, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.8 }}
          transition={{ duration: 0.3 }}
          onClick={handleClick}
          className="floating-cta-button"
          aria-label={t('FloatingCTA.ariaLabel')}
        >
          <motion.div
            animate={{
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              repeatType: 'loop',
            }}
            className="floating-cta-icon"
          >
            <FiMessageCircle size={24} />
          </motion.div>
          <span className="floating-cta-text">{t('FloatingCTA.label')}</span>
        </motion.button>
      )}
    </AnimatePresence>
  );
}
